import { useState, useEffect, useCallback, useRef } from 'react';
import {
  notificationService,
  UnreadNotification
} from '../services/notificationService';
import {
  createNotificationSSEClient,
  NotificationSSEClient,
  TitleUpdateEvent 
} from '../services/notificationSSEService';

/**
 * 未读通知 Hook 返回值
 */
export interface UseUnreadNotificationsResult {
  /** 未读通知列表 */
  readonly notifications: readonly UnreadNotification[];
  /** 未读数量 */
  readonly count: number;
  /** 是否正在加载 */
  readonly loading: boolean;
  /** 错误信息 */
  readonly error: Error | null;
  /** 是否已连接 SSE */
  readonly connected: boolean;
  /** 刷新通知列表（强制从服务器获取） */
  readonly refresh: () => Promise<void>;
  /** 标记单个为已读 */
  readonly markAsRead: (taskId: number) => Promise<void>;
  /** 标记全部为已读 */
  readonly markAllAsRead: () => Promise<void>;
  /** 手动重连 SSE */
  readonly reconnect: () => void;
}

/**
 * Hook 配置选项
 */
export interface UseUnreadNotificationsOptions {
  /** 是否启用 SSE（默认 true） */
  readonly enableSSE?: boolean;
  /** 降级轮询间隔（毫秒），SSE 失败时使用，默认 30 秒，设为 0 禁用 */
  readonly fallbackPollingInterval?: number;
  /** 用户 ID */
  readonly userId?: string;
  /** 标题更新回调 */
  readonly onTitleUpdate?: (event: TitleUpdateEvent) => void;
  /** 业务类型，用于过滤通知 */
  readonly businessType?: string;
}

/**
 * 未读通知 Hook（SSE 版本）
 * 
 * @param options 配置选项
 */
export const useUnreadNotifications = (
  options: UseUnreadNotificationsOptions = {}
): UseUnreadNotificationsResult => {
  const {
    enableSSE = true,
    fallbackPollingInterval = 30000,
    userId,
    onTitleUpdate,
    businessType
  } = options;

  const [notifications, setNotifications] = useState<readonly UnreadNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [connected, setConnected] = useState(false);

  const sseClientRef = useRef<NotificationSSEClient | null>(null);
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const onTitleUpdateRef = useRef(onTitleUpdate);

  // 保持回调引用最新
  useEffect(() => {
    onTitleUpdateRef.current = onTitleUpdate;
  }, [onTitleUpdate]);

  // 清理轮询定时器
  const clearPollingTimer = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, []);

  // 启动降级轮询
  const startFallbackPolling = useCallback(() => {
    if (fallbackPollingInterval <= 0) return;
    
    clearPollingTimer();
    
    pollingTimerRef.current = setInterval(async () => {
      try {
        const result = await notificationService.getUnreadNotifications(20, businessType);
        setNotifications(result.notifications);
      } catch (err) {
        console.error('[useUnreadNotifications] 轮询失败:', err);
      }
    }, fallbackPollingInterval);
  }, [fallbackPollingInterval, clearPollingTimer, businessType]);

  // 手动刷新
  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const result = await notificationService.getUnreadNotifications(20, businessType);
      setNotifications(result.notifications);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      console.error('[useUnreadNotifications] refresh failed:', err);
    } finally {
      setLoading(false);
    }
  }, [businessType]);

  // 标记已读
  const markAsRead = useCallback(async (taskId: number): Promise<void> => {
    try {
      await notificationService.markAsRead(taskId);
      // 乐观更新：立即从列表中移除
      setNotifications(prev => prev.filter(n => n.taskId !== taskId));
    } catch (err) {
      console.error('[useUnreadNotifications] markAsRead failed:', err);
      throw err;
    }
  }, []);

  // 标记全部已读
  const markAllAsRead = useCallback(async (): Promise<void> => {
    const taskIds = notifications.map(n => n.taskId);
    if (taskIds.length === 0) return;

    try {
      await notificationService.markAllAsRead(taskIds);
      // 乐观更新：清空列表
      setNotifications([]);
    } catch (err) {
      console.error('[useUnreadNotifications] markAllAsRead failed:', err);
      throw err;
    }
  }, [notifications]);

  // 手动重连
  const reconnect = useCallback(() => {
    sseClientRef.current?.reconnect();
  }, []);

  // SSE 连接管理
  useEffect(() => {
    if (!enableSSE) {
      // 不启用 SSE，使用轮询
      void refresh();
      // startFallbackPolling();
      return () => {
        clearPollingTimer();
      };
    }

    // 创建 SSE 连接
    const client = createNotificationSSEClient({
      userId,
      businessType,
      onConnected: () => {
        console.log('[useUnreadNotifications] SSE 已连接');
        setConnected(true);
        setError(null);
        clearPollingTimer(); // 连接成功，停止轮询
      },
      onNotification: (notification) => {
        // 收到新通知，添加到列表（去重）
        setNotifications(prev => {
          const exists = prev.some(n => n.taskId === notification.taskId);
          if (exists) return prev;
          return [notification, ...prev];
        });
      },
      onRead: (taskId) => {
        // 其他客户端标记已读，同步移除
        setNotifications(prev => prev.filter(n => n.taskId !== taskId));
      },
      onReadAll: (taskIds) => {
        // 其他客户端批量标记已读
        const taskIdSet = new Set(taskIds);
        setNotifications(prev => prev.filter(n => !taskIdSet.has(n.taskId)));
      },
      // 新增：处理标题更新
      onTitleUpdate: (event) => {
        console.log('[useUnreadNotifications] 收到标题更新:', event);
        // 更新通知列表中的标题
        setNotifications(prev => 
          prev.map(n => 
            n.conversationId === event.conversationId
              ? { ...n, conversationTitle: event.title }
              : n
          )
        );
        // 调用外部回调
        onTitleUpdateRef.current?.(event);
      },
      onDisconnect: () => {
        console.log('[useUnreadNotifications] SSE 已断开');
        setConnected(false);
        // 断开后启动降级轮询
        // startFallbackPolling();
      },
      onError: (err) => {
        console.error('[useUnreadNotifications] SSE 错误:', err);
        setError(err);
      }
    });

    sseClientRef.current = client;

    // 清理函数
    return () => {
      client.close();
      sseClientRef.current = null;
      clearPollingTimer();
    };
  }, [enableSSE, userId, businessType, refresh, startFallbackPolling, clearPollingTimer]);

  return {
    notifications,
    count: notifications.length,
    loading,
    error,
    connected,
    refresh,
    markAsRead,
    markAllAsRead,
    reconnect
  };
};